import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Plus, X, Calendar, CalendarRange, Zap, Megaphone, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { fetchKitchenSettings, updateKitchenSettings } from "@/lib/api";

function buildSlotsFor(startHm, endHm) {
  const toMin = (hm) => {
    const [h, m] = hm.split(":").map(Number);
    return h * 60 + m;
  };
  const start = toMin(startHm);
  const end = toMin(endHm);
  const out = [];
  for (let t = start; t <= end; t += 30) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    const ampm = h < 12 ? "AM" : "PM";
    let h12 = h > 12 ? h - 12 : h;
    if (h12 === 0) h12 = 12;
    out.push(`${h12}:${String(m).padStart(2, "0")} ${ampm}`);
  }
  return out;
}

function isoWeekdayBackend(iso) {
  // JS 0=Sun..6=Sat -> backend 0=Mon..6=Sun
  const d = new Date(iso + "T12:00:00");
  const js = d.getDay();
  return js === 0 ? 6 : js - 1;
}

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function next14Days() {
  const out = [];
  const today = new Date();
  today.setHours(0,0,0,0);
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push({
      iso: d.toISOString().slice(0, 10),
      weekday: d.getDay(), // 0=Sun..6=Sat
      label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
    });
  }
  return out;
}

function nextWeekDates() {
  // Return next week Mon..Sun with their ISO dates. "Next week" = the 7-day block
  // starting the Monday after this week's Sunday.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // JS 0=Sun..6=Sat. Monday of this week:
  const jsDow = today.getDay();
  const daysSinceMon = jsDow === 0 ? 6 : jsDow - 1;
  const thisMon = new Date(today);
  thisMon.setDate(today.getDate() - daysSinceMon);
  const nextMon = new Date(thisMon);
  nextMon.setDate(thisMon.getDate() + 7);
  const names = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(nextMon);
    d.setDate(nextMon.getDate() + i);
    out.push({
      iso: d.toISOString().slice(0, 10),
      wd: i, // backend 0=Mon..6=Sun
      name: names[i],
      short: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    });
  }
  return out;
}

function datesBetween(startIso, endIso) {
  const out = [];
  const s = new Date(startIso + "T12:00:00");
  const e = new Date(endIso + "T12:00:00");
  if (isNaN(s) || isNaN(e) || e < s) return out;
  const cur = new Date(s);
  while (cur <= e) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function KitchenSettings({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [meta, setMeta] = useState([]);
  const [newClosedDate, setNewClosedDate] = useState("");
  const [vacStart, setVacStart] = useState("");
  const [vacEnd, setVacEnd] = useState("");
  const [slotDate, setSlotDate] = useState("");
  const [expandedDay, setExpandedDay] = useState("");

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchKitchenSettings(token);
      setSettings(data.settings);
      setMeta(data.menu_meta);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (id, patch) => {
    setSettings(s => ({ ...s, items: { ...s.items, [id]: { ...s.items[id], ...patch } } }));
  };
  const updateHours = (wd, patch) => {
    setSettings(s => ({ ...s, hours: { ...s.hours, [wd]: { ...s.hours[wd], ...patch } } }));
  };
  const addClosedDate = () => {
    if (!newClosedDate) return;
    setSettings(s => ({ ...s, closed_dates: Array.from(new Set([...(s.closed_dates || []), newClosedDate])).sort() }));
    setNewClosedDate("");
  };
  const removeClosedDate = (d) => {
    setSettings(s => ({ ...s, closed_dates: s.closed_dates.filter(x => x !== d) }));
  };

  const addVacationRange = () => {
    if (!vacStart || !vacEnd) return toast.error("Pick both start and end dates");
    const range = datesBetween(vacStart, vacEnd);
    if (!range.length) return toast.error("End date must be after start date");
    setSettings(s => ({ ...s, closed_dates: Array.from(new Set([...(s.closed_dates || []), ...range])).sort() }));
    setVacStart(""); setVacEnd("");
    toast.success(`Closed ${range.length} day${range.length === 1 ? "" : "s"}`);
  };

  const reopenVacationRange = () => {
    if (!vacStart || !vacEnd) return toast.error("Pick both start and end dates");
    const range = datesBetween(vacStart, vacEnd);
    if (!range.length) return toast.error("End date must be after start date");
    const rangeSet = new Set(range);
    setSettings(s => ({
      ...s,
      closed_dates: (s.closed_dates || []).filter(d => !rangeSet.has(d)),
    }));
    setVacStart(""); setVacEnd("");
    toast.success(`Reopened ${range.length} day${range.length === 1 ? "" : "s"}`);
  };

  const toggleSlotDisabled = (iso, slot, disabled) => {
    setSettings(s => {
      const cur = { ...(s.disabled_slots || {}) };
      const list = new Set(cur[iso] || []);
      if (disabled) {
        list.add(slot);
      } else {
        list.delete(slot);
      }
      if (list.size) {
        cur[iso] = Array.from(list).sort();
      } else {
        delete cur[iso];
      }
      return { ...s, disabled_slots: cur };
    });
  };

  const closeNextNDays = (n) => {
    if (!window.confirm(`Close the next ${n} day${n === 1 ? "" : "s"} to new orders?`)) return;
    const today = new Date();
    const dates = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d.toISOString().slice(0, 10));
    }
    setSettings(s => ({ ...s, closed_dates: Array.from(new Set([...(s.closed_dates || []), ...dates])).sort() }));
    toast.success(`Closed the next ${n} days · tap Save to apply`);
  };

  const setSlotCap = (v) => {
    setSettings(s => ({ ...s, slot_cap: v }));
  };

  const setDailyCap = (v) => {
    setSettings(s => ({ ...s, daily_order_cap: v }));
  };

  const toggleNextWeek = (v) => {
    setSettings(s => ({ ...s, next_week_open: v }));
  };

  const toggleOrdersClosed = (v) => {
    setSettings(s => ({ ...s, orders_closed: v }));
  };

  const setClosedMessage = (v) => {
    setSettings(s => ({ ...s, closed_message: v }));
  };

  const toggleDayOpen = (iso, isOpen) => {
    setSettings(s => {
      const closed = new Set(s.closed_dates || []);
      if (isOpen) {
        closed.delete(iso);
      } else {
        closed.add(iso);
      }
      return { ...s, closed_dates: Array.from(closed).sort() };
    });
  };

  const upcoming = useMemo(() => next14Days(), []);

  const save = async () => {
    setSaving(true);
    try {
      const capValue = settings.daily_order_cap;
      const capNumber = capValue === "" || capValue == null ? null : Number(capValue);
      const slotCapValue = settings.slot_cap;
      const slotCapNumber = slotCapValue === "" || slotCapValue == null ? null : Number(slotCapValue);
      const payload = {
        items: Object.fromEntries(Object.entries(settings.items).map(([k, v]) => ([k, {
          name: v.name,
          price: Number(v.price),
          description: v.description || "",
          sold_out: !!v.sold_out,
          active: !!v.active,
          daily_limit: v.daily_limit === "" || v.daily_limit == null ? null : Number(v.daily_limit),
        }]))),
        hours: settings.hours,
        closed_dates: settings.closed_dates,
        disabled_slots: settings.disabled_slots || {},
        clear_daily_cap: capNumber == null || capNumber <= 0,
        daily_order_cap: capNumber && capNumber > 0 ? capNumber : null,
        clear_slot_cap: slotCapNumber == null || slotCapNumber <= 0,
        slot_cap: slotCapNumber && slotCapNumber > 0 ? slotCapNumber : null,
        next_week_open: !!settings.next_week_open,
        orders_closed: !!settings.orders_closed,
        closed_message: settings.closed_message || "",
      };
      const fresh = await updateKitchenSettings(payload, token);
      setSettings(fresh);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="text-center py-20">
        <Loader2 className="w-10 h-10 mx-auto text-[#D9381E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-16" data-testid="kitchen-settings-panel">
      {/* Broadcast / Orders closed */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-2 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#D9381E]" /> Orders closed (broadcast banner)
        </h2>
        <p className="text-sm text-[#7A6C5D] mb-3">
          Turn on to show a bright banner at the top of the site letting customers know orders are closed. Ordering is blocked while this is on. The banner links to Instagram and Facebook.
        </p>
        <div className={`rounded-2xl border p-4 ${settings.orders_closed ? "bg-[#FDECE8] border-[#D9381E]" : "bg-white border-[#E5D9C5]"}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              data-testid="open-till-otherwise-btn"
              onClick={() => { toggleOrdersClosed(false); setClosedMessage(""); }}
              className={`rounded-xl px-3 py-3 font-display font-extrabold text-sm flex items-center justify-center gap-2 border-2 transition-colors ${!settings.orders_closed ? "bg-[#2C5E3B] text-white border-[#2C5E3B]" : "bg-white text-[#2C5E3B] border-[#2C5E3B] hover:bg-[#2C5E3B]/10"}`}
            >
              Open till otherwise
            </button>
            <button
              type="button"
              data-testid="closed-till-otherwise-btn"
              onClick={() => toggleOrdersClosed(true)}
              className={`rounded-xl px-3 py-3 font-display font-extrabold text-sm flex items-center justify-center gap-2 border-2 transition-colors ${settings.orders_closed ? "bg-[#D9381E] text-white border-[#D9381E]" : "bg-white text-[#D9381E] border-[#D9381E] hover:bg-[#D9381E]/10"}`}
            >
              Closed till otherwise
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-bold text-[#2C241B]">Show "Orders closed" banner</div>
              <div className="text-xs text-[#7A6C5D]">{settings.orders_closed ? "On · site is showing the closed banner" : "Off · site is accepting orders"}</div>
            </div>
            <Switch
              data-testid="orders-closed-toggle"
              checked={!!settings.orders_closed}
              onCheckedChange={toggleOrdersClosed}
            />
          </div>
          <div className="mt-3">
            <Label className="text-xs font-semibold">Custom message (optional)</Label>
            <Input
              data-testid="closed-message-input"
              placeholder="Back Tuesday! Follow us for updates."
              value={settings.closed_message || ""}
              onChange={e => setClosedMessage(e.target.value)}
              className="mt-1 border-[#E5D9C5]"
              maxLength={200}
            />
          </div>
          <div className="mt-2 text-[11px] text-[#7A6C5D]">
            Tip: Click one of the big buttons above, then hit Save at the bottom to apply it right away.
          </div>
        </div>
      </section>

      {/* Menu items */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-extrabold text-xl text-[#2C241B]">Menu items</h2>
          <button
            data-testid="settings-save-btn"
            onClick={save}
            disabled={saving}
            className="cta-primary rounded-full px-5 py-2 font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {meta.map(m => {
            const it = settings.items[m.id] || {};
            return (
              <div key={m.id} data-testid={`settings-item-${m.id}`} className="bg-white rounded-2xl border border-[#E5D9C5] p-5 space-y-3">
                <div className="flex items-center gap-3">
                  {m.image ? (
                    <img src={m.image} alt="" className={`w-14 h-14 object-cover rounded-xl ${(m.id === 'pork_plate' || m.id === 'chicken_kelaguen') ? 'plate-img-rotate' : ''}`} />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-[#F5EEE0] border border-[#E5D9C5] flex items-center justify-center text-[#7A6C5D] text-xs font-semibold">
                      {m.category}
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      data-testid={`settings-name-${m.id}`}
                      value={it.name || ""}
                      onChange={e => updateItem(m.id, { name: e.target.value })}
                      className="font-display font-bold border-[#E5D9C5]"
                    />
                    <div className="text-[11px] text-[#7A6C5D] mt-1">{m.category} · id: {m.id}</div>
                  </div>
                </div>

                <Input
                  data-testid={`settings-desc-${m.id}`}
                  placeholder="Description"
                  value={it.description || ""}
                  onChange={e => updateItem(m.id, { description: e.target.value })}
                  className="border-[#E5D9C5]"
                />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Price ($)</Label>
                    <Input
                      data-testid={`settings-price-${m.id}`}
                      type="number"
                      step="0.01"
                      value={it.price}
                      onChange={e => updateItem(m.id, { price: e.target.value })}
                      className="mt-1 border-[#E5D9C5]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Daily limit</Label>
                    <Input
                      data-testid={`settings-limit-${m.id}`}
                      type="number"
                      min="0"
                      placeholder="unlimited"
                      value={it.daily_limit ?? ""}
                      onChange={e => updateItem(m.id, { daily_limit: e.target.value })}
                      className="mt-1 border-[#E5D9C5]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 text-sm font-semibold">
                    <Switch
                      data-testid={`settings-active-${m.id}`}
                      checked={!!it.active}
                      onCheckedChange={v => updateItem(m.id, { active: v })}
                    />
                    Show on menu
                  </label>
                  <label className="flex items-center gap-2 text-sm font-semibold">
                    <Switch
                      data-testid={`settings-soldout-${m.id}`}
                      checked={!!it.sold_out}
                      onCheckedChange={v => updateItem(m.id, { sold_out: v })}
                    />
                    Sold out
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Weekly hours */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-4">Weekly hours</h2>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-4 space-y-2">
          {WEEKDAY_NAMES.map((name, idx) => {
            const wd = String(idx);
            const h = settings.hours[wd] || { closed: false, start: "11:00", end: "20:00" };
            return (
              <div key={wd} data-testid={`hours-row-${wd}`} className="grid grid-cols-12 items-center gap-2 py-2 border-b last:border-b-0 border-[#F5EEE0]">
                <div className="col-span-4 sm:col-span-3 font-semibold text-sm">{name}</div>
                <div className="col-span-3 sm:col-span-2 flex items-center gap-2">
                  <Switch
                    data-testid={`hours-open-${wd}`}
                    checked={!h.closed}
                    onCheckedChange={v => updateHours(wd, { closed: !v })}
                  />
                  <span className="text-xs font-semibold">{h.closed ? "Closed" : "Open"}</span>
                </div>
                <div className="col-span-5 sm:col-span-7 flex items-center gap-2">
                  <Input
                    data-testid={`hours-start-${wd}`}
                    type="time"
                    value={h.start}
                    disabled={h.closed}
                    onChange={e => updateHours(wd, { start: e.target.value })}
                    className="border-[#E5D9C5] text-sm"
                  />
                  <span className="text-xs text-[#7A6C5D]">to</span>
                  <Input
                    data-testid={`hours-end-${wd}`}
                    type="time"
                    value={h.end}
                    disabled={h.closed}
                    onChange={e => updateHours(wd, { end: e.target.value })}
                    className="border-[#E5D9C5] text-sm"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Per-slot cap */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-2 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#D9381E]" /> Max orders per pickup time
        </h2>
        <p className="text-sm text-[#7A6C5D] mb-3">
          Each 30-minute pickup slot can accept up to this many orders. When a slot fills up, it hides from customers automatically. Cancelling an order reopens the slot instantly. Leave blank for no limit. Kitchen staff can still add or edit orders in this dashboard even if a slot is full.
        </p>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-4 flex items-center gap-3">
          <Input
            data-testid="slot-cap-input"
            type="number"
            min="0"
            placeholder="No cap"
            value={settings.slot_cap ?? ""}
            onChange={e => setSlotCap(e.target.value)}
            className="max-w-[140px] border-[#E5D9C5]"
          />
          <span className="text-sm text-[#7A6C5D]">orders per slot</span>
        </div>
      </section>

      {/* Auto-close (daily order cap) */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-2 flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#D9381E]" /> Auto-close after X orders / day
        </h2>
        <p className="text-sm text-[#7A6C5D] mb-3">Safety net: when a day hits this many orders, new customers see "fully booked" and are asked to pick another day. Leave blank for no limit.</p>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-4 flex items-center gap-3">
          <Input
            data-testid="daily-order-cap-input"
            type="number"
            min="0"
            placeholder="No cap"
            value={settings.daily_order_cap ?? ""}
            onChange={e => setDailyCap(e.target.value)}
            className="max-w-[140px] border-[#E5D9C5]"
          />
          <span className="text-sm text-[#7A6C5D]">orders per day</span>
        </div>
      </section>

      {/* Next-week accepting orders toggle */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-2">Accepting orders for next week</h2>
        <p className="text-sm text-[#7A6C5D] mb-3">Off: customers can only book this week (through Sunday). On: customers can book two weeks ahead.</p>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-4 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-[#2C241B]">Next week bookings</div>
            <div className="text-xs text-[#7A6C5D]">{settings.next_week_open ? "On · customers can book next week" : "Off · only this week is bookable"}</div>
          </div>
          <Switch
            data-testid="next-week-toggle"
            checked={!!settings.next_week_open}
            onCheckedChange={toggleNextWeek}
          />
        </div>
      </section>

      {/* Next 14 days: day on/off + per-slot toggle */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-2 flex items-center gap-2">
          <CalendarRange className="w-5 h-5 text-[#2C5E3B]" /> Next 14 days · days & time slots
        </h2>
        <p className="text-sm text-[#7A6C5D] mb-3">
          Toggle any day on/off with the switch. Tap a day to expand and turn individual 30-minute slots off (for a lunch break, private event, etc.) without closing the whole day. Remember to Save.
        </p>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-3 sm:p-4 space-y-2" data-testid="next-14-days-grid">
          {upcoming.map(({ iso, weekday, label }) => {
            // JS 0=Sun..6=Sat -> backend 0=Mon..6=Sun
            const backendWd = weekday === 0 ? 6 : weekday - 1;
            const hd = settings.hours[String(backendWd)] || { closed: false, start: "11:00", end: "20:00" };
            const dayClosed = !!hd.closed;
            const dateClosed = (settings.closed_dates || []).includes(iso);
            const isOpen = !dayClosed && !dateClosed;
            const slots = dayClosed ? [] : buildSlotsFor(hd.start, hd.end);
            const disabledSet = new Set((settings.disabled_slots || {})[iso] || []);
            const disabledCount = slots.filter(s => disabledSet.has(s)).length;
            const isExpanded = expandedDay === iso;
            return (
              <div
                key={iso}
                data-testid={`day-toggle-row-${iso}`}
                className={`rounded-xl border transition-colors ${isOpen ? "bg-[#F9F6F0] border-[#E5D9C5]" : "bg-[#FDECE8] border-[#D9381E]/40"}`}
              >
                <div className="flex items-center gap-2 px-3 py-2">
                  <button
                    type="button"
                    data-testid={`expand-day-${iso}`}
                    onClick={() => setExpandedDay(isExpanded ? "" : iso)}
                    disabled={!isOpen}
                    className="flex-1 text-left flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isOpen ? (isExpanded ? <ChevronUp className="w-4 h-4 text-[#7A6C5D]" /> : <ChevronDown className="w-4 h-4 text-[#7A6C5D]" />) : <X className="w-4 h-4 text-[#D9381E]" />}
                    <div>
                      <div className="text-sm font-semibold text-[#2C241B]">{label}</div>
                      <div className="text-[11px] text-[#7A6C5D]">
                        {dayClosed
                          ? "Closed by weekly hours"
                          : dateClosed
                            ? "Closed for the day"
                            : disabledCount > 0
                              ? `${slots.length - disabledCount} of ${slots.length} slots open`
                              : `${slots.length} slots open`}
                      </div>
                    </div>
                  </button>
                  <Switch
                    data-testid={`day-toggle-${iso}`}
                    checked={isOpen}
                    disabled={dayClosed}
                    onCheckedChange={v => toggleDayOpen(iso, v)}
                  />
                </div>
                {isExpanded && isOpen && (
                  <div className="border-t border-[#E5D9C5] px-3 py-2.5 bg-white/60 rounded-b-xl">
                    <div className="text-[11px] font-semibold text-[#7A6C5D] mb-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> Tap to turn a time off / on</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5" data-testid={`slots-grid-${iso}`}>
                      {slots.map(s => {
                        const off = disabledSet.has(s);
                        return (
                          <button
                            key={s}
                            data-testid={`slot-toggle-${iso}-${s.replace(/[\s:]/g, "-")}`}
                            onClick={() => toggleSlotDisabled(iso, s, !off)}
                            className={`rounded-full px-2 py-1 text-[11px] font-semibold border-2 transition-colors ${off ? "border-[#D9381E] bg-[#FDECE8] text-[#D9381E] line-through" : "border-[#E5D9C5] bg-white text-[#2C241B] hover:bg-[#F5EEE0]"}`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Disable specific time slots */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-2 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#2C5E3B]" /> Turn off individual time slots
        </h2>
        <p className="text-sm text-[#7A6C5D] mb-3">
          Block specific 30-minute pickup times on a single day without closing the whole day. Great for a lunch break or a private event.
        </p>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-4 space-y-4" data-testid="disable-slots-panel">
          <div>
            <Label className="text-xs font-semibold">Pick a date</Label>
            <Input
              data-testid="slot-date-input"
              type="date"
              value={slotDate}
              onChange={e => setSlotDate(e.target.value)}
              className="mt-1 border-[#E5D9C5] max-w-[220px]"
            />
          </div>
          {slotDate && (() => {
            const wd = String(isoWeekdayBackend(slotDate));
            const hd = settings.hours[wd] || { start: "11:00", end: "20:00", closed: false };
            if (hd.closed) {
              return <div className="text-sm text-[#7A6C5D]">This weekday is closed by weekly hours. No slots to configure.</div>;
            }
            const slots = buildSlotsFor(hd.start, hd.end);
            const disabled = new Set((settings.disabled_slots || {})[slotDate] || []);
            return (
              <div>
                <div className="text-xs font-semibold text-[#7A6C5D] mb-2">
                  Slots for {new Date(slotDate + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="slots-grid">
                  {slots.map(s => {
                    const isDisabled = disabled.has(s);
                    return (
                      <button
                        key={s}
                        data-testid={`slot-toggle-${slotDate}-${s.replace(/[\s:]/g, "-")}`}
                        onClick={() => toggleSlotDisabled(slotDate, s, !isDisabled)}
                        className={`rounded-full px-3 py-2 text-xs font-semibold border-2 transition-colors ${isDisabled ? "border-[#D9381E] bg-[#FDECE8] text-[#D9381E] line-through" : "border-[#E5D9C5] bg-white text-[#2C241B] hover:bg-[#F5EEE0]"}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 text-[11px] text-[#7A6C5D]">
                  Tap a slot to toggle it. Red / crossed-out slots are turned off — customers won't see them. Remember to Save.
                </div>
              </div>
            );
          })()}
          {(() => {
            const entries = Object.entries(settings.disabled_slots || {}).filter(([, v]) => v && v.length);
            if (!entries.length) return null;
            return (
              <div className="border-t border-[#F5EEE0] pt-3">
                <div className="text-xs font-semibold text-[#7A6C5D] mb-2">Currently disabled</div>
                <div className="space-y-1.5" data-testid="disabled-slots-summary">
                  {entries.sort().map(([iso, list]) => (
                    <div key={iso} className="text-sm">
                      <span className="font-semibold text-[#2C241B]">{iso}</span>
                      <span className="text-[#7A6C5D]"> · {list.join(", ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Closed dates */}
      <section>
        <h2 className="font-display font-extrabold text-xl text-[#2C241B] mb-4">Closed dates (holidays / days off)</h2>
        <div className="bg-white rounded-2xl border border-[#E5D9C5] p-4 space-y-4">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs font-semibold">Pick a single date</Label>
              <Input
                data-testid="closed-date-input"
                type="date"
                value={newClosedDate}
                onChange={e => setNewClosedDate(e.target.value)}
                className="mt-1 border-[#E5D9C5]"
              />
            </div>
            <button
              data-testid="closed-date-add-btn"
              onClick={addClosedDate}
              className="cta-primary rounded-full px-4 py-2 font-semibold text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>

          <div className="border-t border-[#F5EEE0] pt-4">
            <div className="text-xs font-semibold text-[#2C5E3B] mb-2 flex items-center gap-1.5">
              <CalendarRange className="w-4 h-4" /> Quick close
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="close-next-7-btn"
                onClick={() => closeNextNDays(7)}
                className="rounded-full px-4 py-2 text-xs font-bold border-2 border-[#D9381E] text-[#D9381E] hover:bg-[#D9381E] hover:text-white transition-colors"
              >
                Close next 7 days
              </button>
              <button
                data-testid="close-next-14-btn"
                onClick={() => closeNextNDays(14)}
                className="rounded-full px-4 py-2 text-xs font-bold border-2 border-[#E5D9C5] text-[#2C241B] hover:bg-[#F5EEE0] transition-colors"
              >
                Close next 14 days
              </button>
            </div>
          </div>

          <div className="border-t border-[#F5EEE0] pt-4">
            <div className="text-xs font-semibold text-[#2C5E3B] mb-2 flex items-center gap-1.5">
              <CalendarRange className="w-4 h-4" /> Vacation range
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className="text-xs font-semibold">Start</Label>
                <Input
                  data-testid="vacation-start-input"
                  type="date"
                  value={vacStart}
                  onChange={e => setVacStart(e.target.value)}
                  className="mt-1 border-[#E5D9C5]"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">End</Label>
                <Input
                  data-testid="vacation-end-input"
                  type="date"
                  value={vacEnd}
                  onChange={e => setVacEnd(e.target.value)}
                  className="mt-1 border-[#E5D9C5]"
                />
              </div>
              <button
                data-testid="vacation-add-btn"
                onClick={addVacationRange}
                className="cta-primary rounded-full px-4 py-2 font-semibold text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Close range
              </button>
              <button
                data-testid="vacation-reopen-btn"
                onClick={reopenVacationRange}
                className="rounded-full px-4 py-2 font-semibold text-sm border-2 border-[#2C5E3B] text-[#2C5E3B] hover:bg-[#2C5E3B] hover:text-white transition-colors flex items-center gap-1"
              >
                <X className="w-4 h-4" /> Reopen range
              </button>
            </div>
          </div>

          <div className="border-t border-[#F5EEE0] pt-4">
            <div className="text-xs font-semibold text-[#7A6C5D] mb-2">Currently closed</div>
            <div className="flex flex-wrap gap-2">
              {(settings.closed_dates || []).length === 0 && <div className="text-sm text-[#7A6C5D]">No closed dates.</div>}
              {(settings.closed_dates || []).map(d => (
                <div key={d} data-testid={`closed-date-${d}`} className="flex items-center gap-2 bg-[#F5EEE0] border border-[#E5D9C5] rounded-full pl-3 pr-1 py-1 text-sm">
                  <Calendar className="w-3.5 h-3.5" /> {d}
                  <button data-testid={`closed-date-remove-${d}`} onClick={() => removeClosedDate(d)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center hover:bg-[#D9381E] hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="sticky bottom-4 z-20 flex justify-end">
        <button
          data-testid="settings-save-btn-bottom"
          onClick={save}
          disabled={saving}
          className="cta-primary rounded-full px-6 py-3 font-display font-extrabold text-sm flex items-center gap-2 shadow-2xl disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </div>
  );
}
